import { ChevronDown } from '@pay-com/icons'
import { ReactNode, useEffect, useState } from 'react'
import AnimateHeight from 'react-animate-height'

type ExpandableSectionProps = {
  open?: boolean
  startIcon?: ReactNode
  title: ReactNode
  secondaryTitle?: ReactNode
  expandable?: boolean
  expandButtonLabel?: ReactNode
  badgeCount?: string
  children: ReactNode
}

export const ExpandableSection = ({
  startIcon,
  title,
  secondaryTitle,
  open = false,
  expandable = true,
  expandButtonLabel,
  badgeCount,
  children
}: ExpandableSectionProps) => {
  const [height, setHeight] = useState<'auto' | number>(open ? 'auto' : 0)
  const isOpen = height !== 0

  useEffect(() => {
    if (!expandable) setHeight(0)
  }, [expandable])

  const handleClick = () => {
    if (!expandable) return
    setHeight(prev => (prev === 0 ? 'auto' : 0))
  }

  const Wrapper = expandable ? 'button' : 'div'

  return (
    <div className="rounded-lg border p-2">
      <Wrapper
        {...(expandable
          ? { type: 'button' as const, onClick: handleClick }
          : {})}
        className={`flex w-full items-center justify-between px-4 py-2 ${expandable ? 'cursor-pointer' : ''}`}
      >
        <div className="flex shrink-0 items-center gap-1">
          {startIcon && <div>{startIcon}</div>}
          <div className="font-semibold">{title}</div>
          {badgeCount && +badgeCount > 0 && (
            <div className="flex size-5 items-center justify-center rounded-full bg-red-500 text-xs text-white">
              {badgeCount}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {expandButtonLabel}
          {expandable && (
            <ChevronDown
              className={`size-6 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : 'rotate-0'}`}
              color="#334155"
            />
          )}
        </div>
      </Wrapper>
      {secondaryTitle && (
        <div className="px-4 pb-2 text-sm text-gray-600">{secondaryTitle}</div>
      )}
      <AnimateHeight duration={250} height={height}>
        <div className="px-4 pb-5 pt-0.5">{children}</div>
      </AnimateHeight>
    </div>
  )
}
